import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Percent, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API } from "./admin-utils";

/**
 * Combined Availability + Pricing dashboard.
 *
 * One row per menu item. Columns:
 *   - Item (sticky-left)
 *   - Public (availability checkbox only; price uses base)
 *   - One column per location:
 *        [ ☑ enabled ]
 *        [ £ override input ]   (only when enabled)
 *   - Row actions (sticky-right): all-on / all-off
 *
 * State model — everything is queued client-side and saved via a single
 * "Save Changes" bar. Two PUT calls fire in parallel:
 *   - PUT /api/admin/availability-matrix   (is_public + available_locations)
 *   - PUT /api/admin/pricing-matrix        (location_prices)
 * so we reuse the existing endpoints untouched.
 */
export function MenuMatrixTab() {
  const [data, setData] = useState({ locations: [], categories: [], items: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  // availDirty[item_id] = { is_public, available_locations }
  const [availDirty, setAvailDirty] = useState({});
  // priceDirty[item_id][slug] = "" (clear override) | "3.50" (set) | undefined (untouched)
  const [priceDirty, setPriceDirty] = useState({});
  // Multi-select for bulk toggling many items at once
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [availRes, priceRes] = await Promise.all([
        axios.get(`${API}/admin/availability-matrix`),
        axios.get(`${API}/admin/pricing-matrix`),
      ]);
      // Merge the two payloads — locations & categories are identical.
      // Pricing payload has base_price + location_prices; availability payload has is_public.
      const priceMap = new Map(priceRes.data.items.map((it) => [it.id, it]));
      const items = availRes.data.items.map((it) => {
        const p = priceMap.get(it.id) || {};
        return {
          id: it.id,
          name: it.name,
          category: it.category,
          week_number: it.week_number,
          is_public: it.is_public !== false,
          available_locations: it.available_locations || [],
          base_price: p.base_price ?? 0,
          location_prices: p.location_prices || {},
        };
      });
      setData({
        locations: availRes.data.locations,
        categories: availRes.data.categories,
        items,
      });
      setAvailDirty({});
      setPriceDirty({});
      setSelectedIds(new Set());
    } catch {
      toast.error("Failed to load matrix");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filteredItems = useMemo(() => {
    const q = search.toLowerCase().trim();
    return data.items.filter((it) => {
      if (categoryFilter !== "all" && it.category !== categoryFilter) return false;
      if (q && !it.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data.items, categoryFilter, search]);

  // --- current-state getters (dirty layered on top of persisted) ---

  const availOf = (item) => {
    if (availDirty[item.id]) return availDirty[item.id];
    return {
      is_public: item.is_public,
      available_locations: item.available_locations,
    };
  };

  const priceOf = (item, slug) => {
    if (priceDirty[item.id] && slug in priceDirty[item.id]) {
      return priceDirty[item.id][slug];
    }
    const v = item.location_prices?.[slug];
    return v === undefined ? "" : String(v);
  };

  // --- writes into dirty state ---

  const setAvail = (item, patch) => {
    setAvailDirty((d) => ({
      ...d,
      [item.id]: { ...availOf(item), ...patch },
    }));
  };

  const setPrice = (itemId, slug, value) => {
    setPriceDirty((d) => ({
      ...d,
      [itemId]: { ...(d[itemId] || {}), [slug]: value },
    }));
  };

  const clearPrice = (itemId, slug) => setPrice(itemId, slug, "");

  const togglePublic = (item) =>
    setAvail(item, { is_public: !availOf(item).is_public });

  const toggleLocation = (item, slug) => {
    const cur = availOf(item);
    const enabled = cur.available_locations.includes(slug);
    const next = enabled
      ? cur.available_locations.filter((s) => s !== slug)
      : [...cur.available_locations, slug];
    setAvail(item, { available_locations: next });
    // NOTE: we intentionally do NOT clear the saved location_prices override on
    // disable — historical subsidy prices stay in the DB so re-enabling the item
    // later restores them without the admin having to re-type the price.
  };

  // --- column / row / cell bulk actions ---

  const bulkColumn = (target, enable) => {
    const updates = { ...availDirty };
    filteredItems.forEach((it) => {
      const cur = updates[it.id] || {
        is_public: it.is_public,
        available_locations: it.available_locations,
      };
      if (target === "public") {
        updates[it.id] = { ...cur, is_public: enable };
      } else {
        const has = cur.available_locations.includes(target);
        let next = cur.available_locations;
        if (enable && !has) next = [...cur.available_locations, target];
        if (!enable && has) next = cur.available_locations.filter((s) => s !== target);
        updates[it.id] = { ...cur, available_locations: next };
      }
    });
    setAvailDirty(updates);
    // Historical prices are preserved on disable — do not clear location_prices.
  };

  const bulkRow = (item, enable) => {
    const allSlugs = data.locations.map((l) => l.slug);
    setAvail(item, {
      is_public: enable,
      available_locations: enable ? allSlugs : [],
    });
    // Historical location prices are preserved on 'all off' so re-enabling
    // restores the previously-agreed subsidy without re-entry.
  };

  const applySubsidyToColumn = (slug) => {
    const raw = window.prompt(
      `Apply a % discount to ALL enabled items at this location?\n\n` +
        `Enter discount % (e.g. 20 = 20% off base price).\nLeave blank to cancel.`
    );
    if (!raw) return;
    const pct = parseFloat(raw);
    if (Number.isNaN(pct) || pct < 0 || pct >= 100) {
      toast.error("Enter a valid % between 0 and 99.99");
      return;
    }
    setPriceDirty((d) => {
      const next = { ...d };
      for (const it of filteredItems) {
        const cur = availOf(it);
        if (!cur.available_locations.includes(slug)) continue;
        const newPrice = Math.max(0, it.base_price * (1 - pct / 100));
        next[it.id] = { ...(next[it.id] || {}), [slug]: newPrice.toFixed(2) };
      }
      return next;
    });
    toast.success(`Queued ${pct}% subsidy on visible enabled items at this location`);
  };

  const clearColumnPrices = (slug) => {
    setPriceDirty((d) => {
      const next = { ...d };
      for (const it of filteredItems) {
        next[it.id] = { ...(next[it.id] || {}), [slug]: "" };
      }
      return next;
    });
    toast.message("Queued: clear overrides at this location for visible items");
  };

  const setColumnFinalPrice = (slug) => {
    const raw = window.prompt(
      `Set a final £ price for ALL enabled items at this location?\n\n` +
        `Every enabled item's override becomes this exact price. Leave blank to cancel.`
    );
    if (!raw) return;
    const n = parseFloat(raw);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Enter a valid price");
      return;
    }
    setPriceDirty((d) => {
      const next = { ...d };
      for (const it of filteredItems) {
        const cur = availOf(it);
        if (!cur.available_locations.includes(slug)) continue;
        next[it.id] = { ...(next[it.id] || {}), [slug]: n.toFixed(2) };
      }
      return next;
    });
    toast.success(`Queued £${n.toFixed(2)} on visible enabled items at this location`);
  };

  const discardAll = () => {
    setAvailDirty({});
    setPriceDirty({});
  };

  // --- multi-item selection ---

  const visibleIds = useMemo(() => filteredItems.map((it) => it.id), [filteredItems]);
  const visibleSelectedCount = useMemo(
    () => visibleIds.filter((id) => selectedIds.has(id)).length,
    [visibleIds, selectedIds]
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFilteredSelection = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  /**
   * Bulk enable / disable every LOCATION (+ optionally Public) for all selected items.
   * Historical location_prices are preserved (never cleared).
   */
  const applySelectionToAllLocations = (enable) => {
    if (selectedIds.size === 0) return;
    const allSlugs = data.locations.map((l) => l.slug);
    setAvailDirty((d) => {
      const next = { ...d };
      for (const id of selectedIds) {
        const item = data.items.find((it) => it.id === id);
        if (!item) continue;
        const cur = next[id] || {
          is_public: item.is_public,
          available_locations: item.available_locations,
        };
        next[id] = {
          is_public: enable,
          available_locations: enable ? allSlugs : [],
        };
        // preserve cur reference to avoid an unused warning
        void cur;
      }
      return next;
    });
    toast.success(
      `Queued: ${enable ? "enable" : "disable"} ${selectedIds.size} item${
        selectedIds.size === 1 ? "" : "s"
      } across all locations`
    );
  };

  const dirtyItemCount = useMemo(() => {
    const ids = new Set([...Object.keys(availDirty), ...Object.keys(priceDirty)]);
    return ids.size;
  }, [availDirty, priceDirty]);

  const saveAll = async () => {
    if (dirtyItemCount === 0) return;

    // Validate all queued prices first
    const priceUpdates = [];
    for (const [itemId, locMap] of Object.entries(priceDirty)) {
      for (const [slug, val] of Object.entries(locMap)) {
        const trimmed = String(val).trim();
        if (trimmed === "") {
          priceUpdates.push({ item_id: itemId, location_slug: slug, price: null });
        } else {
          const n = parseFloat(trimmed);
          if (Number.isNaN(n) || n < 0) {
            toast.error(`Invalid price on "${slug}": "${val}"`);
            return;
          }
          priceUpdates.push({ item_id: itemId, location_slug: slug, price: n });
        }
      }
    }

    const availUpdates = Object.entries(availDirty).map(([item_id, state]) => ({
      item_id,
      is_public: state.is_public,
      available_locations: state.available_locations,
    }));

    setSaving(true);
    try {
      const calls = [];
      if (availUpdates.length) {
        calls.push(
          axios.put(`${API}/admin/availability-matrix`, { updates: availUpdates })
        );
      }
      if (priceUpdates.length) {
        calls.push(
          axios.put(`${API}/admin/pricing-matrix`, { updates: priceUpdates })
        );
      }
      await Promise.all(calls);
      toast.success(
        `Saved ${dirtyItemCount} item${dirtyItemCount === 1 ? "" : "s"}`
      );
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#1aa59a]" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="menu-matrix-tab">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="matrix-category-filter">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {data.categories.map((c) => (
              <SelectItem key={c.key} value={c.key}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="Search items..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1"
          data-testid="matrix-search"
        />
      </div>

      <p className="text-xs text-gray-500">
        Toggle a location to enable an item there. Leave the £ input blank to use the base
        price; enter a number to charge a location-specific price. <b>Disabled prices are
        remembered</b> — turning a location back on restores the previously-agreed price.
        Tick the checkboxes at the left to bulk-enable or bulk-disable many items across
        every location at once. Changes queue until you click <b>Save</b>.
      </p>

      {data.locations.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-3">
          No locations yet. Create locations first, then come back here to manage them.
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-20">
              <tr>
                <th className="p-2 text-center sticky left-0 bg-gray-50 z-30 w-10 border-r">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllFilteredSelection}
                    className="w-4 h-4 cursor-pointer accent-[#1aa59a]"
                    data-testid="matrix-select-all"
                    aria-label="Select all visible items"
                    title="Select all filtered items"
                  />
                </th>
                <th className="text-left p-2 sticky left-10 bg-gray-50 z-30 min-w-[210px] w-[270px] border-r">
                  Item
                </th>
                <th className="p-2 text-center min-w-[90px] border-r">
                  <div className="font-medium">Public</div>
                  <div className="text-[10px] text-gray-400">
                    base £ (rack)
                  </div>
                  <div className="flex gap-1 justify-center mt-1">
                    <button
                      className="text-[10px] text-[#1aa59a] hover:underline"
                      onClick={() => bulkColumn("public", true)}
                    >
                      ✓all
                    </button>
                    <button
                      className="text-[10px] text-red-500 hover:underline"
                      onClick={() => bulkColumn("public", false)}
                    >
                      ✗all
                    </button>
                  </div>
                </th>
                {data.locations.map((loc) => (
                  <th key={loc.slug} className="p-2 text-center min-w-[140px]">
                    <div className="font-medium truncate max-w-[160px] mx-auto">
                      {loc.name}
                    </div>
                    <div className="flex gap-1 justify-center mt-1">
                      <button
                        className="text-[10px] text-[#1aa59a] hover:underline"
                        onClick={() => bulkColumn(loc.slug, true)}
                        data-testid={`col-enable-${loc.slug}`}
                      >
                        ✓all
                      </button>
                      <button
                        className="text-[10px] text-red-500 hover:underline"
                        onClick={() => bulkColumn(loc.slug, false)}
                        data-testid={`col-disable-${loc.slug}`}
                      >
                        ✗all
                      </button>
                    </div>
                    <div className="flex gap-1 justify-center mt-0.5 text-[10px]">
                      <button
                        type="button"
                        className="text-gray-600 hover:underline inline-flex items-center gap-0.5"
                        onClick={() => setColumnFinalPrice(loc.slug)}
                        data-testid={`col-set-price-${loc.slug}`}
                      >
                        set £
                      </button>
                      <button
                        type="button"
                        className="text-gray-600 hover:underline inline-flex items-center gap-0.5"
                        onClick={() => applySubsidyToColumn(loc.slug)}
                        data-testid={`col-pct-${loc.slug}`}
                      >
                        <Percent className="w-2.5 h-2.5" />%
                      </button>
                      <button
                        type="button"
                        className="text-gray-500 hover:underline"
                        onClick={() => clearColumnPrices(loc.slug)}
                        data-testid={`col-clear-${loc.slug}`}
                      >
                        clear £
                      </button>
                    </div>
                  </th>
                ))}
                <th className="p-2 text-center min-w-[100px] sticky right-0 bg-gray-50 z-20 border-l">
                  Row
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={data.locations.length + 4}
                    className="text-center text-gray-400 py-6"
                  >
                    No items match the current filter.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const availState = availOf(item);
                  const rowIsDirty = !!availDirty[item.id] || !!priceDirty[item.id];
                  const isSelected = selectedIds.has(item.id);
                  const rowBg = isSelected
                    ? "bg-teal-50"
                    : rowIsDirty
                    ? "bg-yellow-50"
                    : "bg-white hover:bg-gray-50";
                  return (
                    <tr key={item.id} className={`border-t ${rowBg}`}>
                      <td
                        className={`p-2 text-center sticky left-0 z-10 border-r ${rowBg}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRowSelection(item.id)}
                          className="w-4 h-4 cursor-pointer accent-[#1aa59a]"
                          data-testid={`matrix-row-select-${item.id}`}
                          aria-label={`Select ${item.name}`}
                        />
                      </td>
                      <td
                        className={`p-2 sticky left-10 z-10 border-r ${rowBg}`}
                        data-testid={`matrix-row-${item.id}`}
                      >
                        <div className="font-medium truncate max-w-[260px]">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {data.categories.find((c) => c.key === item.category)?.label ||
                            item.category}
                          {item.week_number ? ` · W${item.week_number}` : ""}
                          {" · "}£{item.base_price.toFixed(2)}
                        </div>
                      </td>
                      <td className="p-2 text-center border-r">
                        <input
                          type="checkbox"
                          checked={availState.is_public}
                          onChange={() => togglePublic(item)}
                          className="w-4 h-4 cursor-pointer accent-[#1aa59a]"
                          data-testid={`cell-public-${item.id}`}
                        />
                      </td>
                      {data.locations.map((loc) => {
                        const enabled = availState.available_locations.includes(
                          loc.slug
                        );
                        const priceVal = priceOf(item, loc.slug);
                        return (
                          <td
                            key={loc.slug}
                            className={`p-1 text-center align-middle ${
                              enabled ? "" : "bg-gray-50/60"
                            }`}
                          >
                            <div className="flex items-center justify-center gap-1">
                              <input
                                type="checkbox"
                                checked={enabled}
                                onChange={() => toggleLocation(item, loc.slug)}
                                className="w-4 h-4 cursor-pointer accent-[#1aa59a]"
                                data-testid={`cell-toggle-${item.id}-${loc.slug}`}
                              />
                              {enabled ? (
                                <div className="flex items-center gap-1">
                                  <div className="relative">
                                    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">
                                      £
                                    </span>
                                    <Input
                                      type="number"
                                      inputMode="decimal"
                                      min={0}
                                      step="0.01"
                                      placeholder={item.base_price.toFixed(2)}
                                      value={priceVal}
                                      onChange={(e) =>
                                        setPrice(item.id, loc.slug, e.target.value)
                                      }
                                      className="h-7 w-20 pl-5 pr-1 text-xs text-right font-mono"
                                      data-testid={`cell-price-${item.id}-${loc.slug}`}
                                    />
                                  </div>
                                  {priceVal !== "" && (
                                    <button
                                      type="button"
                                      onClick={() => clearPrice(item.id, loc.slug)}
                                      className="text-gray-400 hover:text-rose-500"
                                      title="Reset to base price"
                                      data-testid={`cell-reset-${item.id}-${loc.slug}`}
                                    >
                                      <RotateCcw className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span
                                  className="text-[10px] text-gray-400 italic pl-1"
                                  data-testid={`cell-off-${item.id}-${loc.slug}`}
                                >
                                  {item.location_prices?.[loc.slug] !== undefined
                                    ? `off · £${Number(item.location_prices[loc.slug]).toFixed(2)} saved`
                                    : "off"}
                                </span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td
                        className={`p-2 text-center sticky right-0 z-10 border-l ${rowBg}`}
                      >
                        <div className="flex gap-1 justify-center">
                          <button
                            className="text-[10px] text-[#1aa59a] hover:underline"
                            onClick={() => bulkRow(item, true)}
                            data-testid={`row-all-on-${item.id}`}
                          >
                            all on
                          </button>
                          <button
                            className="text-[10px] text-red-500 hover:underline"
                            onClick={() => bulkRow(item, false)}
                            data-testid={`row-all-off-${item.id}`}
                          >
                            all off
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div
          className="sticky bottom-16 bg-white border-t border-b border-[#1aa59a]/40 shadow-md p-3 flex flex-wrap items-center gap-2 rounded-lg -mx-4 sm:mx-0 z-30"
          data-testid="matrix-selection-bar"
        >
          <button
            type="button"
            onClick={clearSelection}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            title="Clear selection"
            data-testid="matrix-clear-selection"
          >
            ✕
          </button>
          <span className="text-sm font-medium text-gray-800">
            {selectedIds.size} item{selectedIds.size === 1 ? "" : "s"} selected
          </span>
          <span className="text-xs text-gray-500 hidden sm:inline">
            (bulk actions apply to selected items only)
          </span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            className="border-[#1aa59a] text-[#1aa59a] hover:bg-[#1aa59a]/10"
            onClick={() => applySelectionToAllLocations(true)}
            data-testid="matrix-selection-enable-all"
          >
            Enable at all locations
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-red-400 text-red-500 hover:bg-red-50"
            onClick={() => applySelectionToAllLocations(false)}
            data-testid="matrix-selection-disable-all"
          >
            Disable at all locations
          </Button>
        </div>
      )}

      {dirtyItemCount > 0 && (
        <div
          className="sticky bottom-0 bg-white border-t shadow-lg p-3 flex flex-wrap items-center justify-between gap-3 rounded-b-lg -mx-4 sm:mx-0 z-30"
          data-testid="matrix-save-bar"
        >
          <span className="text-sm font-medium text-amber-700">
            ⚡ {dirtyItemCount} item{dirtyItemCount === 1 ? "" : "s"} with unsaved changes
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={discardAll} disabled={saving}>
              Discard
            </Button>
            <Button
              size="sm"
              onClick={saveAll}
              disabled={saving}
              className="bg-[#1aa59a] hover:bg-[#158a80]"
              data-testid="matrix-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
